import { useEffect, useState } from 'react';
import { monthlyEquivalent, annualEquivalent, type Cadence } from '@heivol-finance/calc';
import { budgetApi, type BudgetData, type Category } from '../lib/data.js';
import { formatOre, formatPercent } from '../lib/format.js';
import {
  PageHeader,
  Card,
  StatCard,
  DeleteButton,
  TextField,
  OreField,
  Select,
} from '../components/ui.js';
import { SectionBar, Tr, Th, Td, TdNum } from '../components/table.js';

const CADENCE_OPTS: { value: Cadence; label: string }[] = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annual', label: 'Annual' },
  { value: 'custom', label: 'Other' },
];

export function Budget(): JSX.Element {
  const [data, setData] = useState<BudgetData | null>(null);
  const reload = () => budgetApi.load().then(setData);
  useEffect(() => {
    void reload();
  }, []);

  if (!data) {
    return (
      <div>
        <PageHeader title="Budget" subtitle="Loading…" />
      </div>
    );
  }

  const s = data.summary;
  const expenseCats = data.categories.filter((c) => c.kind === 'expense');
  const catName = (id: string | null) => data.categories.find((c) => c.id === id)?.name ?? '—';

  // Category rollup (monthly), for the expense table's subtotal panel.
  const rollup = new Map<string, number>();
  for (const e of data.expenses) {
    const key = catName(e.category_id);
    rollup.set(key, (rollup.get(key) ?? 0) + monthlyEquivalent(e.planned_ore, e.cadence));
  }
  const totalExpenseMonthly = s.expenseMonthly || 1;

  const catOptions = [{ value: '', label: 'No category' }, ...expenseCats.map((c: Category) => ({ value: c.id, label: c.name }))];

  return (
    <div className="max-w-5xl">
      <PageHeader
        title="Budget"
        subtitle={
          <>
            Monthly household — the baseline for your whole net worth. Reproduces the spreadsheet's{' '}
            <span className="font-mono">Common</span> tab.
          </>
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard label="Income / mo." value={formatOre(s.incomeMonthly)} tone="success" />
        <StatCard label="Expenses / mo." value={formatOre(s.expenseMonthly)} />
        <StatCard
          label="Net / mo."
          value={formatOre(s.cashNet)}
          tone={s.cashNet >= 0 ? 'ai' : 'warning'}
          hint="Total after expenses"
        />
        <StatCard
          label="Net-worth baseline / mo."
          value={formatOre(s.netWorthBaseline)}
          hint="Net + savings (not lost)"
        />
      </div>

      {/* Income */}
      <Card className="mb-6 overflow-hidden">
        <SectionBar
          title="Income"
          onAdd={() => budgetApi.addIncome({ name: 'New income', amount_ore: 0, cadence: 'monthly' }).then(reload)}
        />
        <div className="overflow-x-auto">
          <table className="w-full text-hv-sm">
            <thead>
              <Tr head>
                <Th>Name</Th>
                <Th>Frequency</Th>
                <Th right>Amount</Th>
                <Th right>Per month</Th>
                <Th right>Per year</Th>
                <Th />
              </Tr>
            </thead>
            <tbody>
              {data.incomes.map((i) => (
                <Tr key={i.id}>
                  <Td>
                    <TextField value={i.name} onCommit={(v) => budgetApi.updateIncome(i.id, { name: v }).then(reload)} />
                  </Td>
                  <Td>
                    <Select
                      value={i.cadence}
                      options={CADENCE_OPTS}
                      onChange={(v) => budgetApi.updateIncome(i.id, { cadence: v }).then(reload)}
                    />
                  </Td>
                  <Td right>
                    <OreField value={i.amount_ore} onCommit={(v) => budgetApi.updateIncome(i.id, { amount_ore: v }).then(reload)} />
                  </Td>
                  <TdNum>{formatOre(monthlyEquivalent(i.amount_ore, i.cadence))}</TdNum>
                  <TdNum>{formatOre(annualEquivalent(i.amount_ore, i.cadence))}</TdNum>
                  <Td>
                    <DeleteButton onClick={() => budgetApi.deleteIncome(i.id).then(reload)} />
                  </Td>
                </Tr>
              ))}
              {data.incomes.length === 0 && (
                <Tr>
                  <Td colSpan={6}>
                    <span className="text-hv-text-tertiary">No income yet.</span>
                  </Td>
                </Tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Expenses */}
      <Card className="mb-6 overflow-hidden">
        <SectionBar
          title="Expenses"
          onAdd={() => budgetApi.addExpense({ name: 'New expense', planned_ore: 0, cadence: 'monthly' }).then(reload)}
        />
        <div className="overflow-x-auto">
          <table className="w-full text-hv-sm">
            <thead>
              <Tr head>
                <Th>Name</Th>
                <Th>Category</Th>
                <Th>Frequency</Th>
                <Th right>Amount</Th>
                <Th right>Per month</Th>
                <Th right>% of expenses</Th>
                <Th center>Savings</Th>
                <Th />
              </Tr>
            </thead>
            <tbody>
              {data.expenses.map((e) => {
                const m = monthlyEquivalent(e.planned_ore, e.cadence);
                return (
                  <Tr key={e.id}>
                    <Td>
                      <TextField value={e.name} onCommit={(v) => budgetApi.updateExpense(e.id, { name: v }).then(reload)} />
                    </Td>
                    <Td>
                      <Select
                        value={e.category_id ?? ''}
                        options={catOptions}
                        onChange={(v) => budgetApi.updateExpense(e.id, { category_id: (v || null) as string | null }).then(reload)}
                      />
                    </Td>
                    <Td>
                      <Select
                        value={e.cadence}
                        options={CADENCE_OPTS}
                        onChange={(v) => budgetApi.updateExpense(e.id, { cadence: v }).then(reload)}
                      />
                    </Td>
                    <Td right>
                      <OreField value={e.planned_ore} onCommit={(v) => budgetApi.updateExpense(e.id, { planned_ore: v }).then(reload)} />
                    </Td>
                    <TdNum>{formatOre(m)}</TdNum>
                    <TdNum>{formatPercent(m / totalExpenseMonthly)}</TdNum>
                    <Td center>
                      <input
                        type="checkbox"
                        checked={e.is_investment_contribution}
                        onChange={(ev) => budgetApi.updateExpense(e.id, { is_investment_contribution: ev.target.checked }).then(reload)}
                        aria-label="Savings / investment"
                        className="h-4 w-4 accent-[color:var(--hv-ai)]"
                      />
                    </Td>
                    <Td>
                      <DeleteButton onClick={() => budgetApi.deleteExpense(e.id).then(reload)} />
                    </Td>
                  </Tr>
                );
              })}
              {data.expenses.length === 0 && (
                <Tr>
                  <Td colSpan={8}>
                    <span className="text-hv-text-tertiary">No expenses yet.</span>
                  </Td>
                </Tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Category rollup */}
      {rollup.size > 0 && (
        <Card className="p-4">
          <div className="mb-3 text-hv-sm font-medium text-hv-text">Categories (per month)</div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {[...rollup.entries()]
              .sort((a, b) => b[1] - a[1])
              .map(([name, ore]) => (
                <div key={name} className="flex items-center justify-between rounded-hv-md bg-hv-surface-alt px-3 py-2 text-hv-sm">
                  <span className="text-hv-text-secondary">{name}</span>
                  <span className="tabular-nums text-hv-text">{formatOre(ore)}</span>
                </div>
              ))}
          </div>
        </Card>
      )}
    </div>
  );
}
